import { AbstractControl, UntypedFormControl, UntypedFormGroup, ValidationErrors } from '@angular/forms';
import { LoDash } from '../utils/angular-utils/lodash-utils';

interface FormUtilsControlValues {
  [control: string]: any;
}

export interface FormUtilsControlState {
  name: string;
  status: string;
  valid: boolean;
  pending: boolean;
  enabled: boolean;
  dirty: boolean;
  touched: boolean;
  hasError: any;
  errors: ValidationErrors;
  warnings: any;
  value?: any;
  children?: FormUtilsControlState[];
}

/**
 * Set a series of FormControl values, maintaining a sentinal for
 * tracking while the setValues are happening.
 */
export class FormUtils {
  /**
   * Returns object representing the control's state
   */
  public static getControlState(control: AbstractControl, controlName: string = null, recursive = false): FormUtilsControlState {
    const result: FormUtilsControlState = {
      name: controlName,
      status: control.status,
      valid: control.valid,
      pending: control.pending,
      enabled: control.enabled,
      errors: control.errors,
      warnings: control['warnings'],
      dirty: control.dirty,
      touched: control.touched,
      hasError: control.hasError,
    };
    if (control instanceof UntypedFormControl) {
      LoDash.set(result, 'value', control.value);
    }

    if (recursive) {
      LoDash.set(
        result,
        'children',
        LoDash.get(control, 'controls', []).map((c: any, name: any) => this.getControlState(c, name, recursive))
      );
    }
    return result;
  }

  /**
   * Sets the specified control in the group to enabled/disabled without emitting events
   */
  public static setEnabled(group: UntypedFormGroup, field: string, isEnabled: boolean = true) {
    const control = group.get(field);
    if (control) {
      if (isEnabled) {
        control.enable({ onlySelf: true, emitEvent: false });
      } else {
        control.disable({ onlySelf: true, emitEvent: false });
      }
    }
  }

  /**
   * Set the values for the named controls if it is different, without emitting statusChanges
   * and valueChanges events
   */
  public static setValues(form: AbstractControl, values: FormUtilsControlValues) {
    for (const [controlKey, value] of Object.entries(values)) {
      const control = form.get(controlKey);
      FormUtils.setValue(control, value);
    }
  }

  /**
   * Set the value for the control if it is different, without emitting statusChanges
   * and valueChanges events
   */
  public static setValue(control: AbstractControl, value: any) {
    if (control && !LoDash.isEqual(control.value, value)) {
      control.patchValue(value, { emitEvent: false }); // don't emit change events
      control.markAsDirty();
    }
    if (control) {
      control.markAsTouched();
    }
  }

  /**
   * Set warning for the control and warningtype passed in
   */
  public static setWarning(control: any, warningType: string, warning: Object) {
    LoDash.set(control, `warnings[${warningType}]`, warning);
  }

  /**
   * Remove warning for the control and warningtype passed in
   */
  public static unsetWarning(control: AbstractControl, warningType: string) {
    if (control['warnings']?.[`${warningType}`]) {
      LoDash.unset(control, `warnings[${warningType}]`);
    }
  }

  /**
   * Mark the passed control as touched. If recursive, mark all of its children as touched
   */
  public static markAsTouched(control: AbstractControl, recursive: boolean = true) {
    control.markAsTouched();
    if (recursive) {
      const controls = LoDash.get(control, 'controls', {});
      Object.keys(controls).forEach(c => FormUtils.markAsTouched(controls[c], recursive));
    }
  }

  /**
   * Return true if the passed control or named control in the passed group has warnings
   * @param group control or group to test
   * @param field name of control in group to test. If not provided, returns if group has warnings
   */
  public static hasWarnings(group: AbstractControl, field?: string): boolean {
    if (group && group.enabled) {
      const control = field ? group.get(field) : group;
      return !!control && control.enabled && !LoDash.isEmpty(LoDash.get(control, 'warnings'));
    }
    return false;
  }

  public static hasWarning(group: AbstractControl, field: string, warning: string): boolean {
    if (group && group.enabled) {
      const control = field ? group.get(field) : group;
      return control && control.enabled && !!LoDash.get(control, `warnings[${warning}]`);
    }
    return false;
  }

  public static getWarning(group: UntypedFormGroup, field: string, warning: string): any {
    const control = field ? group.get(field) : group;
    return LoDash.getWithRegexp(control, `warnings[${warning}]`);
  }

  public static clearWarnings(control: AbstractControl, warnings: string[] = null) {
    if (control && LoDash.has(control, 'warnings')) {
      if (warnings) {
        (LoDash.castArray(warnings) ?? []).forEach(warning => {
          delete control['warnings'][warning];
        });
      } else {
        delete control['warnings'];
      }
    }
  }

  public static hasErrors(group: UntypedFormGroup, field: string): boolean {
    if (group && group.enabled) {
      const control = group.get(field);
      return control && control.enabled && control.invalid;
    }
    return false;
  }

  public static hasError(group: UntypedFormGroup, field: string, error: string): boolean {
    if (group && group.enabled) {
      const control = group.get(field);
      return control && control.enabled && control.hasError(error);
    }
    return false;
  }
}
